import { useState } from "react";

import { Building2, Filter, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../../../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";

const VendorsTab = ({
  vendors,
  fetchVendorData,
  onEditVendor,
  filters,
  onFilterChange,
  showDeleted,
  setShowDeleted,
  setVendorPage
}) => {
  const { user } = useAuth();
  const api = useApi();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingVendor, setDeletingVendor] = useState(null);

  const openDeleteDialog = (vendor) => {
    setDeletingVendor(vendor);
    setIsDeleteOpen(true);
  };

  const deleteVendor = async () => {
    try {
      const response = await api.delete(`/vendors/${deletingVendor.id}`);
      if (response.status === 200 || response.status === 204) {
        toast.success("Vendor deleted successfully!");
        setIsDeleteOpen(false);
        setDeletingVendor(null);
        fetchVendorData();
      } else {
        toast.error("Failed to delete vendor");
      }
    } catch (error) {
      toast.error(error.message || "Error deleting vendor");
    }
  };

  const vendorColumns = [
    {
      header: "Vendor Name",
      accessor: "vendorName",
      isPrimary: true,
      render: (val, vendor) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100">{val}</span>
          {vendor.deleted && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
              Deleted
            </span>
          )}
        </div>
      )
    },
    {
      header: "Type",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters?.type && filters.type !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Type</p>
              <Select value={filters?.type || "ALL"} onValueChange={(val) => onFilterChange("type", val)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  <SelectItem value="material_supplier">Material Vendor</SelectItem>
                  <SelectItem value="equipment_rental">Equipment Rental</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "vendorType",
      render: (val, vendor) => (
        <span className="capitalize text-sm text-slate-600 dark:text-slate-400">
          {val === "other" && vendor.otherType ? vendor.otherType : val?.replace("_", " ")}
        </span>
      )
    },
    {
      header: "Contact",
      accessor: "phone",
      render: (_, vendor) => (
        <div className="text-xs space-y-1">
          <div className="text-slate-600 dark:text-slate-400 font-medium">{vendor.phone || "No phone"}</div>
          <div className="text-slate-500 dark:text-slate-500 italic">{vendor.email || "No email"}</div>
        </div>
      )
    },
    {
      header: "Payment Terms",
      accessor: "paymentTerms",
      render: (val) => val?.replace("_", " ").toUpperCase(),
      cellClassName: "text-xs font-bold text-yale-blue-600 dark:text-yale-blue-400"
    },
    {
      header: "Status",
      accessor: "status",
      render: (val) => (
        <span
          className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full uppercase tracking-wide ${
            val === "active"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
          }`}
        >
          {val}
        </span>
      )
    },
    {
      header: "Actions",
      filter: user?.permissions?.vendor?.delete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setVendorPage(0);
                }}
                id="show-deleted-vendors-table"
              />
              <Label htmlFor="show-deleted-vendors-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, vendor) => (
        <ActionMenu
          canEdit={user?.permissions?.vendor?.update && !vendor.deleted}
          canDelete={user?.permissions?.vendor?.delete && !vendor.deleted}
          onEdit={() => onEditVendor(vendor)}
          onDelete={() => openDeleteDialog(vendor)}
        />
      )
    }
  ];

  return (
    <div className="flex-1 flex flex-col space-y-4">
      <DataTable
        columns={vendorColumns}
        data={vendors}
        emptyStateTitle="No vendors found"
        emptyStateDescription="Click 'Add Vendor' to register a new vendor or contractor."
        emptyStateIcon={Building2}
        className="flex-1"
      />

      {/* Delete Vendor Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the vendor "{deletingVendor?.vendorName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteVendor} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorsTab;
